/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type RGridGap = 'none' | 'sm' | 'md' | 'lg';
type RGridBreakpoint = 'sm' | 'md' | 'lg';
type RGridAlign = 'start' | 'center' | 'end' | 'stretch';

interface RGridBaseProps {
    children: React.ReactNode;
    /** Gap between grid items: `none` (0), `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: RGridGap;
    /** Collapse to a single column at this breakpoint and below (`down(breakpoint)`). */
    collapseAt?: RGridBreakpoint;
    /** Cross-axis (vertical) alignment of grid items. Defaults to `stretch`. */
    align?: RGridAlign;
    className?: string;
}

interface RGridFixedProps extends RGridBaseProps {
    /** Number of equal columns: `repeat(N, 1fr)`. */
    columns: number;
    minColumnWidth?: never;
    template?: never;
}

interface RGridAutoFillProps extends RGridBaseProps {
    columns?: never;
    /** Auto-fit columns with this minimum width in px: `repeat(auto-fit, minmax(Npx, 1fr))`. Cards expand to fill the row. */
    minColumnWidth: number;
    template?: never;
}

interface RGridTemplateProps extends RGridBaseProps {
    columns?: never;
    minColumnWidth?: never;
    /** Raw `grid-template-columns` value, e.g. `"1fr 360px"`. Escape hatch for asymmetric layouts. */
    template: string;
}

type RGridProps = RGridFixedProps | RGridAutoFillProps | RGridTemplateProps;

const GAP_PX: Record<RGridGap, number> = { none: 0, sm: 8, md: 16, lg: 24 };
const ALIGN_VALUE: Record<RGridAlign, string> = {
    start: 'start',
    center: 'center',
    end: 'end',
    stretch: 'stretch',
};

type StyleProps = {
    $template: string;
    $gap: RGridGap;
    $collapseAt?: RGridBreakpoint;
    $align: RGridAlign;
};

const shouldForward = (prop: string) => !['$template', '$gap', '$collapseAt', '$align'].includes(prop);

const StyledGrid = styled('div', { shouldForwardProp: (p) => shouldForward(p as string) })<StyleProps>(
    ({ $template, $gap, $collapseAt, $align, theme }) => ({
        display: 'grid',
        gridTemplateColumns: $template,
        gap: GAP_PX[$gap],
        alignItems: ALIGN_VALUE[$align],
        ...($collapseAt && {
            [theme.breakpoints.down($collapseAt)]: {
                gridTemplateColumns: '1fr',
            },
        }),
    }),
);

export function RGrid(props: RGridProps) {
    const { children, gap = 'md', collapseAt, align = 'stretch', className } = props;

    let template: string;
    if (props.columns !== undefined) {
        template = `repeat(${props.columns}, minmax(0, 1fr))`;
    } else if (props.minColumnWidth !== undefined) {
        template = `repeat(auto-fit, minmax(${props.minColumnWidth}px, 1fr))`;
    } else {
        template = props.template;
    }

    return (
        <StyledGrid $template={template} $gap={gap} $collapseAt={collapseAt} $align={align} className={className}>
            {children}
        </StyledGrid>
    );
}
/* eslint-enable no-comments/disallowComments */
